// src/pages/auth/Login.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { authService } from 'infrastructure/firebase';
import {
  recordFailedAttempt,
  clearLoginAttempts,
  getLockRemainingSeconds,
  getRemainingAttempts,
  isTrustedDomain,
  isValidEmail,
  isValidCPF,
  formatCPF,
} from 'shared/utils/security';
import { isAdminRole } from 'shared/config/roles';

// ── Rate limiting para reset de senha (client-side, 3 tentativas / 5 min) ──────
const RESET_KEY  = '__pw_reset__';
const RESET_MAX  = 3;
const RESET_LOCK = 5 * 60 * 1000;

function recordResetAttempt() {
  const raw   = JSON.parse(sessionStorage.getItem(RESET_KEY) || '{"count":0,"until":0}');
  const next  = { count: raw.count + 1, until: raw.until };
  if (next.count >= RESET_MAX) next.until = Date.now() + RESET_LOCK;
  sessionStorage.setItem(RESET_KEY, JSON.stringify(next));
  return next;
}
function getResetLockSeconds() {
  const raw = JSON.parse(sessionStorage.getItem(RESET_KEY) || '{"count":0,"until":0}');
  if (!raw.until) return 0;
  const rem = Math.ceil((raw.until - Date.now()) / 1000);
  if (rem <= 0) { sessionStorage.removeItem(RESET_KEY); return 0; }
  return rem;
}
function clearResetAttempts() { sessionStorage.removeItem(RESET_KEY); }

// ── Ícone Google ─────────────────────────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

// ── Modal recuperar senha ─────────────────────────────────────────────────────
function ForgotPassword({ onClose }) {
  const [email, setEmail]         = useState('');
  const [sent, setSent]           = useState(false);
  const [err, setErr]             = useState('');
  const [loading, setLoading]     = useState(false);
  const [lockSecs, setLockSecs]   = useState(() => getResetLockSeconds());

  // Atualiza contador de bloqueio a cada segundo
  React.useEffect(() => {
    if (lockSecs <= 0) return;
    const t = setInterval(() => {
      const r = getResetLockSeconds();
      setLockSecs(r);
      if (r <= 0) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [lockSecs]);

  const handleReset = async (e) => {
    e.preventDefault();
    setErr('');
    const trimmed = email.trim().toLowerCase();
    if (!isValidEmail(trimmed)) {
      setErr('Informe um endereço de e-mail válido.');
      return;
    }
    const lock = getResetLockSeconds();
    if (lock > 0) {
      setLockSecs(lock);
      return;
    }
    setLoading(true);
    try {
      await authService.resetPassword(trimmed);
      clearResetAttempts();
      setSent(true);
    } catch (resetErr) {
      recordResetAttempt();
      const remaining = getResetLockSeconds();
      if (remaining > 0) {
        setLockSecs(remaining);
        setErr(`Muitas tentativas. Aguarde ${Math.ceil(remaining / 60)} min.`);
      } else {
        setErr('Não foi possível enviar o e-mail. Tente novamente.');
      }
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:'28px 24px', width:'100%',
        maxWidth:400, fontFamily:"'DM Sans',sans-serif", boxShadow:'0 8px 32px rgba(0,0,0,0.12)' }}>
        <h2 style={{ fontSize:20, fontWeight:700, color:'#3D3447', marginBottom:8 }}>Recuperar senha</h2>
        {sent ? (
          <>
            <p style={{ color:'#6B6070', fontSize:14, lineHeight:1.6 }}>
              E-mail enviado para <strong>{email}</strong>. Verifique sua caixa de entrada.
            </p>
            <button onClick={onClose} style={{ marginTop:20, width:'100%', padding:12,
              background:'#B5A2D2', color:'#fff', border:'none', borderRadius:10,
              fontSize:14, fontWeight:600, cursor:'pointer' }}>Fechar</button>
          </>
        ) : (
          <form onSubmit={handleReset}>
            <p style={{ color:'#A8A1B0', fontSize:13, marginBottom:16 }}>
              Informe seu e-mail para receber o link de redefinição.
            </p>
            {lockSecs > 0 && (
              <div style={{ padding:'10px 14px', background:'#FFF3CD', border:'1px solid #FBBF24',
                borderRadius:8, color:'#854D0E', fontSize:13, marginBottom:14 }}>
                🔒 Muitas tentativas. Tente em {Math.floor(lockSecs/60)}:{String(lockSecs%60).padStart(2,'0')}
              </div>
            )}
            {err && !lockSecs && <div style={{ padding:'10px 14px', background:'#F0DADD', borderRadius:8,
              color:'#7A3C44', fontSize:13, marginBottom:14 }}>{err}</div>}
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
              placeholder="seu@email.com"
              style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #E8E5EC',
                borderRadius:10, fontSize:14, boxSizing:'border-box', marginBottom:14 }} />
            <div style={{ display:'flex', gap:10 }}>
              <button type="button" onClick={onClose} style={{ flex:1, padding:12,
                background:'#f3f0f7', color:'#6B6070', border:'none', borderRadius:10,
                fontSize:14, cursor:'pointer' }}>Cancelar</button>
              <button type="submit" disabled={loading || lockSecs > 0} style={{ flex:1, padding:12,
                background:'#B5A2D2', color:'#fff', border:'none', borderRadius:10,
                fontSize:14, fontWeight:600,
                cursor: loading || lockSecs > 0 ? 'not-allowed' : 'pointer',
                opacity: loading || lockSecs > 0 ? 0.7 : 1 }}>
                {loading ? 'Enviando...' : 'Enviar link'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Estilos reutilizáveis ─────────────────────────────────────────────────────
const inputStyle = {
  width:'100%', padding:'12px 14px', border:'1.5px solid #E8E5EC', borderRadius:12,
  fontSize:14, fontFamily:"'DM Sans',sans-serif", outline:'none',
  boxSizing:'border-box', color:'#3D3447', background:'#fff',
};

const btnPrimary = (disabled) => ({
  width:'100%', padding:14, background: disabled ? '#A8A1B0' : '#6B5876',
  color:'#fff', border:'none', borderRadius:12, fontSize:15, fontWeight:500,
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily:"'DM Sans',sans-serif", opacity: disabled ? 0.7 : 1,
});

function mapGoogleError(code, message) {
  if (code === 'auth/unauthorized-domain') {
    return 'Domínio não autorizado para Google Sign-In. Acesse pelo endereço oficial do sistema.';
  }
  if (code === 'auth/internal-error' || code === 'auth/operation-not-allowed') {
    return 'Google Sign-In não está habilitado no projeto Firebase. Use e-mail e senha ou contate o administrador.';
  }
  if (code === 'auth/network-request-failed') {
    return 'Sem conexão com a internet. Verifique sua rede e tente novamente.';
  }
  if (code === 'auth/account-exists-with-different-credential') {
    return 'Este e-mail já está cadastrado com outro método. Use e-mail e senha.';
  }
  if (code === 'auth/popup-blocked') {
    return 'O navegador bloqueou o popup. Permita popups para este site e tente novamente.';
  }
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
    return '';
  }
  return `Erro ao entrar com Google (${code || message || 'desconhecido'}).`;
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function Login() {
  const { login, loginWithGoogle, currentUser, userProfile } = useAuth();
  const navigate   = useNavigate();
  const timerRef   = useRef(null);

  // Responsividade
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  // Tabs: 'login' | 'register'
  const [tab, setTab] = useState('login');

  // Estados compartilhados
  const [error, setError]   = useState('');
  const [showForgot, setShowForgot] = useState(false);

  // Login
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [lockSeconds, setLockSeconds]     = useState(() => getLockRemainingSeconds());

  // Cadastro — tipo de perfil: null | 'client' | 'clinic' | 'professional'
  const [regProfileType, setRegProfileType] = useState(null);
  const [regName, setRegName]       = useState('');
  const [regEmail, setRegEmail]     = useState('');
  const [regPhone, setRegPhone]     = useState('');
  const [regCPF, setRegCPF]         = useState('');
  const [regPass, setRegPass]       = useState('');
  const [regPass2, setRegPass2]     = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regDone, setRegDone]       = useState(false);

  // Se usuário já está autenticado (ex: retorno do redirect Google), redireciona
  useEffect(() => {
    if (currentUser && userProfile) {
      redirect(userProfile);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, userProfile]);

  // Erro retornado após redirect Google
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('googleAuthError');
      if (!raw) return;
      sessionStorage.removeItem('googleAuthError');
      const { code, message } = JSON.parse(raw);
      setError(mapGoogleError(code, message));
      setGoogleLoading(false);
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Verificação de domínio
  useEffect(() => {
    if (!isTrustedDomain()) {
      setError('Domínio não autorizado. Acesse pelo endereço oficial do sistema.');
    }
  }, []);

  // Contador de bloqueio
  useEffect(() => {
    if (lockSeconds <= 0) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => {
      const r = getLockRemainingSeconds();
      setLockSeconds(r);
      if (r <= 0) clearInterval(timerRef.current);
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [lockSeconds]);

  const redirect = (profile) => {
    if (isAdminRole(profile?.role))            navigate('/admin/dashboard');
    else if (profile?.role === 'professional') navigate('/pro/dashboard');
    else                                       navigate('/client/home');
  };

  // ── Login e-mail/senha ──────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (getLockRemainingSeconds() > 0) return;
    setLoading(true);
    try {
      const profile = await login(email.trim().toLowerCase(), password);
      clearLoginAttempts();

      // Acesso bloqueado/expirado: entra mesmo assim — a guarda do Layout exibe a
      // tela de bloqueio (com instruções e contato). Antes, deslogávamos com erro,
      // o que escondia a explicação amigável da tela de bloqueio.
      redirect(profile);
    } catch (err) {
      recordFailedAttempt();
      const left   = getRemainingAttempts();
      const locked = getLockRemainingSeconds();
      if (locked > 0) { setLockSeconds(locked); }
      else if (err?.code === 'auth/profile-not-found') {
        setError('Conta autenticada, mas sem perfil no Firestore. Acesse /setup para inicializar ou contate o administrador.');
      } else {
        setError(left > 0
          ? `E-mail ou senha incorretos. ${left} tentativa${left !== 1 ? 's' : ''} restante${left !== 1 ? 's' : ''}.`
          : 'E-mail ou senha incorretos. Tente novamente.');
      }
    } finally { setLoading(false); }
  };

  // ── Login Google ────────────────────────────────────────────────────────────
  const handleGoogle = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      const profile = await loginWithGoogle();
      if (profile) {
        // Bloqueado/expirado: entra e a guarda do Layout mostra a tela de bloqueio.
        redirect(profile);
      } else {
        setGoogleLoading(false);
      }
    } catch (err) {
      const code = err?.code || '';
      const msg = mapGoogleError(code, err?.message);
      if (msg) setError(msg);
      setGoogleLoading(false);
    }
  };

  // ── Cadastro de cliente ─────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!regName.trim() || regName.trim().length < 2) {
      setError('Informe seu nome completo.'); return;
    }
    if (!isValidEmail(regEmail)) {
      setError('Informe um e-mail válido.'); return;
    }
    if (regCPF && !isValidCPF(regCPF)) {
      setError('CPF inválido. Verifique os dígitos informados.'); return;
    }
    if (regPass.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres.'); return;
    }
    if (regPass !== regPass2) {
      setError('As senhas não coincidem.'); return;
    }
    setRegLoading(true);
    try {
      await authService.register({
        name:     regName.trim(),
        email:    regEmail.trim().toLowerCase(),
        password: regPass,
        role:     'client',
        phone:    regPhone.trim(),
        cpf:      regCPF.replace(/\D/g, ''),
        specialty:'',
      });
      setRegDone(true);
    } catch (err) {
      if (err?.code === 'auth/email-already-in-use') {
        setError('Este e-mail já possui cadastro. Faça login.');
      } else if (err?.code === 'auth/weak-password') {
        setError('Senha muito fraca. Use letras, números e símbolos.');
      } else if (err?.code === 'permission-denied') {
        setError('Permissão negada no Firestore. Verifique as regras de segurança ou acesse /setup.');
      } else {
        setError('Erro ao criar conta. Tente novamente.');
      }
    } finally { setRegLoading(false); }
  };

  // Limpa campos de cadastro ao trocar de aba ou tipo de perfil
  const resetRegForm = () => {
    setRegProfileType(null); setRegName(''); setRegEmail(''); setRegPhone('');
    setRegCPF(''); setRegPass(''); setRegPass2(''); setRegDone(false); setError('');
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight:'100vh', display:'flex', background:'#FAF7FB',
      fontFamily:"'DM Sans',sans-serif" }}>
      {showForgot && <ForgotPassword onClose={() => setShowForgot(false)} />}

      {/* Painel de marca — só desktop */}
      {!isMobile && (
        <div style={{ width:400, flexShrink:0, background:'#6B5876', display:'flex',
          flexDirection:'column', justifyContent:'center', padding:60,
          position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', width:300, height:300, background:'#F5D9DC',
            opacity:.22, borderRadius:'50%', top:-80, right:-80 }} />
          <div style={{ position:'absolute', width:200, height:200, background:'#DDB48E',
            opacity:.18, borderRadius:'50%', bottom:-40, left:-40 }} />
          <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:42, color:'#fff',
            position:'relative' }}>
            Agenda<span style={{ color:'#F5D9DC' }}>Pro</span>
          </div>
          <p style={{ fontSize:16, color:'rgba(255,255,255,0.72)', marginTop:12,
            lineHeight:1.6, position:'relative' }}>
            Sistema completo de gestão de agendamentos para clínicas e estúdios.
          </p>
          <div style={{ marginTop:40, display:'flex', flexDirection:'column', gap:16,
            position:'relative' }}>
            {[
              { icon:'✅', label:'Agendamentos online 24h' },
              { icon:'📱', label:'Confirmação automática' },
              { icon:'💰', label:'Controle financeiro' },
              { icon:'👥', label:'Multi-profissional' },
            ].map((item, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:12,
                color:'rgba(255,255,255,0.78)', fontSize:14 }}>
                <span style={{ fontSize:18 }}>{item.icon}</span> {item.label}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Painel do formulário */}
      <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center',
        padding: isMobile ? '24px 20px' : 40, overflowY:'auto' }}>
        <div style={{ width:'100%', maxWidth:420 }}>

          {/* Logo mobile */}
          {isMobile && (
            <div style={{ textAlign:'center', marginBottom:24 }}>
              <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:34, color:'#6B5876' }}>
                Agenda<span style={{ color:'#B5A2D2' }}>Pro</span>
              </div>
              <p style={{ fontSize:13, color:'#A8A1B0', marginTop:4 }}>
                Sistema de gestão de agendamentos
              </p>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display:'flex', background:'#F3F0F7', borderRadius:12,
            padding:4, marginBottom:24 }}>
            {['login','register'].map(t => (
              <button key={t} onClick={() => { setTab(t); resetRegForm(); }}
                style={{ flex:1, padding:'9px 0', border:'none', cursor:'pointer',
                  borderRadius:9, fontSize:14, fontWeight: tab===t ? 600 : 400,
                  fontFamily:"'DM Sans',sans-serif",
                  background: tab===t ? '#fff' : 'transparent',
                  color: tab===t ? '#3D3447' : '#A8A1B0',
                  boxShadow: tab===t ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
                  transition:'all 0.2s' }}>
                {t === 'login' ? 'Entrar' : 'Criar conta'}
              </button>
            ))}
          </div>

          {/* Alerta de bloqueio */}
          {lockSeconds > 0 && (
            <div style={{ padding:'12px 16px', background:'#FFF3CD', border:'1px solid #FBBF24',
              borderRadius:10, color:'#854D0E', fontSize:13, marginBottom:16, textAlign:'center' }}>
              🔒 Acesso temporariamente bloqueado.<br/>
              <strong>Tente em {Math.floor(lockSeconds/60)}:{String(lockSeconds%60).padStart(2,'0')}</strong>
            </div>
          )}

          {/* Erro */}
          {error && !lockSeconds && (
            <div style={{ padding:'11px 14px', background:'#F0DADD', border:'1px solid #C9929A',
              borderRadius:10, color:'#7A3C44', fontSize:13, marginBottom:16 }}>
              {error}
            </div>
          )}

          {/* ── TAB LOGIN ──────────────────────────────────────────────── */}
          {tab === 'login' && (
            <>
              {!isMobile && (
                <>
                  <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:32,
                    marginBottom:4, color:'#3D3447' }}>Bem-vindo</div>
                  <p style={{ color:'#A8A1B0', marginBottom:24, fontSize:14 }}>
                    Entre com suas credenciais para acessar o sistema
                  </p>
                </>
              )}

              {/* Botão Google */}
              <button type="button" onClick={handleGoogle} disabled={googleLoading}
                style={{ width:'100%', padding:'13px 16px', background:'#fff',
                  border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, fontWeight:500,
                  cursor: googleLoading ? 'not-allowed' : 'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center', gap:10,
                  color:'#3D3447', marginBottom:16, fontFamily:"'DM Sans',sans-serif",
                  opacity: googleLoading ? 0.7 : 1 }}>
                <GoogleIcon />
                {googleLoading ? 'Conectando...' : 'Entrar com Google'}
              </button>

              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
                <div style={{ flex:1, height:1, background:'#E8E5EC' }} />
                <span style={{ fontSize:12, color:'#C0BAC8' }}>ou</span>
                <div style={{ flex:1, height:1, background:'#E8E5EC' }} />
              </div>

              <form onSubmit={handleLogin}>
                <div style={{ marginBottom:14 }}>
                  <label style={{ display:'block', fontSize:13, fontWeight:500,
                    color:'#6B6070', marginBottom:6 }}>E-mail</label>
                  <input type="email" value={email}
                    onChange={e => setEmail(e.target.value)} required
                    placeholder="seu@email.com"
                    autoComplete="email"
                    style={inputStyle} />
                </div>
                <div style={{ marginBottom:8 }}>
                  <label style={{ display:'block', fontSize:13, fontWeight:500,
                    color:'#6B6070', marginBottom:6 }}>Senha</label>
                  <input type="password" value={password}
                    onChange={e => setPassword(e.target.value)} required
                    placeholder="••••••••"
                    autoComplete="current-password"
                    style={inputStyle} />
                </div>
                <div style={{ textAlign:'right', marginBottom:20 }}>
                  <button type="button" onClick={() => setShowForgot(true)}
                    style={{ background:'none', border:'none', fontSize:13, color:'#B5A2D2',
                      cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>
                    Esqueci minha senha
                  </button>
                </div>
                <button type="submit" disabled={loading || lockSeconds > 0}
                  style={btnPrimary(loading || lockSeconds > 0)}>
                  {loading ? 'Entrando...' : lockSeconds > 0
                    ? `🔒 Bloqueado (${lockSeconds}s)` : 'Entrar no Sistema →'}
                </button>
              </form>

              <p style={{ textAlign:'center', fontSize:13, color:'#A8A1B0', marginTop:20 }}>
                Ainda não tem conta?{' '}
                <button onClick={() => { setTab('register'); setError(''); }}
                  style={{ background:'none', border:'none', color:'#6B5876', fontWeight:600,
                    cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontSize:13 }}>
                  Cadastre-se grátis
                </button>
              </p>
            </>
          )}

          {/* ── TAB CADASTRO ───────────────────────────────────────────── */}
          {tab === 'register' && (
            <>
              {/* ── Sucesso ── */}
              {regDone ? (
                <div style={{ textAlign:'center', padding:'32px 0' }}>
                  <div style={{ fontSize:48, marginBottom:16 }}>🎉</div>
                  <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:24,
                    color:'#3D3447', marginBottom:8 }}>Conta criada!</div>
                  <p style={{ color:'#6B6070', fontSize:14, lineHeight:1.6, marginBottom:24 }}>
                    Seu cadastro foi realizado com sucesso.<br/>
                    Agora é só entrar no sistema.
                  </p>
                  <button onClick={() => { setTab('login'); resetRegForm(); }} style={btnPrimary(false)}>
                    Ir para o login →
                  </button>
                </div>

              /* ── Passo 1: Seleção de perfil ── */
              ) : !regProfileType ? (
                <>
                  <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:26,
                    color:'#3D3447', marginBottom:6 }}>Criar conta</div>
                  <p style={{ color:'#A8A1B0', fontSize:13, marginBottom:20 }}>Qual é o seu perfil?</p>

                  {[
                    { type:'clinic',       icon:'🏥', title:'Clínica / Estabelecimento',
                      desc:'Salão, clínica de estética, odontologia ou medicina' },
                    { type:'professional', icon:'👩‍⚕️', title:'Profissional',
                      desc:'Autônomo ou colaborador de uma clínica' },
                    { type:'client',       icon:'👤', title:'Cliente',
                      desc:'Quero agendar serviços online' },
                  ].map(opt => (
                    <button key={opt.type}
                      onClick={() => {
                        if (opt.type === 'client') { setRegProfileType('client'); }
                        else if (opt.type === 'clinic') { navigate('/signup/clinic'); }
                        else { navigate('/signup/professional'); }
                      }}
                      style={{ width:'100%', textAlign:'left', padding:'14px 16px',
                        background:'#fff', border:'1.5px solid #E8E5EC', borderRadius:12,
                        cursor:'pointer', marginBottom:10, display:'flex', alignItems:'center',
                        gap:14, fontFamily:"'DM Sans',sans-serif", transition:'border-color 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor='#B5A2D2'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor='#E8E5EC'; }}>
                      <span style={{ fontSize:28, flexShrink:0 }}>{opt.icon}</span>
                      <div>
                        <div style={{ fontSize:14, fontWeight:600, color:'#3D3447' }}>{opt.title}</div>
                        <div style={{ fontSize:12, color:'#A8A1B0', marginTop:2 }}>{opt.desc}</div>
                      </div>
                      <span style={{ marginLeft:'auto', color:'#C0BAC8', fontSize:18 }}>›</span>
                    </button>
                  ))}

                  <p style={{ textAlign:'center', fontSize:13, color:'#A8A1B0', marginTop:16 }}>
                    Já tem conta?{' '}
                    <button onClick={() => { setTab('login'); resetRegForm(); }}
                      style={{ background:'none', border:'none', color:'#6B5876', fontWeight:600,
                        cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontSize:13 }}>
                      Entrar
                    </button>
                  </p>
                </>

              /* ── Clínica / Profissional: placeholder fase 2 ── */
              ) : regProfileType !== 'client' ? (
                <>
                  <button onClick={() => setRegProfileType(null)}
                    style={{ background:'none', border:'none', cursor:'pointer', color:'#A8A1B0',
                      fontSize:13, fontFamily:"'DM Sans',sans-serif", padding:0, marginBottom:20,
                      display:'flex', alignItems:'center', gap:6 }}>
                    ← Voltar
                  </button>
                  <div style={{ textAlign:'center', padding:'20px 0 28px' }}>
                    <div style={{ fontSize:44, marginBottom:14 }}>
                      {regProfileType === 'clinic' ? '🏥' : '👩‍⚕️'}
                    </div>
                    <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:22,
                      color:'#3D3447', marginBottom:8 }}>
                      {regProfileType === 'clinic' ? 'Cadastro de Clínica' : 'Cadastro de Profissional'}
                    </div>
                    <p style={{ color:'#6B6070', fontSize:13, lineHeight:1.7, marginBottom:20 }}>
                      Este módulo estará disponível em breve.<br/>
                      {regProfileType === 'clinic'
                        ? 'Você poderá cadastrar sua clínica, escolher um plano e gerenciar sua equipe.'
                        : 'Você poderá criar seu perfil, configurar serviços e receber agendamentos.'}
                    </p>
                    <div style={{ padding:'14px 16px', background:'#F0EDF3', borderRadius:12,
                      fontSize:13, color:'#6B5876', marginBottom:20, textAlign:'left' }}>
                      <strong>Em breve:</strong>
                      {regProfileType === 'clinic' ? (
                        <ul style={{ margin:'8px 0 0 16px', padding:0, lineHeight:1.8 }}>
                          <li>Cadastro com CNPJ e dados da empresa</li>
                          <li>Categoria: salão, estética, odonto, medicina</li>
                          <li>Planos Básico, Intermediário, Master e Enterprise</li>
                        </ul>
                      ) : (
                        <ul style={{ margin:'8px 0 0 16px', padding:0, lineHeight:1.8 }}>
                          <li>Validação de CRM / CRO</li>
                          <li>Perfil público com foto e descrição</li>
                          <li>Plano individual por R$ 15,00/mês</li>
                        </ul>
                      )}
                    </div>
                    <button onClick={() => setRegProfileType(null)} style={btnPrimary(false)}>
                      ← Escolher outro perfil
                    </button>
                  </div>
                </>

              /* ── Cliente: formulário completo ── */
              ) : (
                <>
                  <button onClick={() => setRegProfileType(null)}
                    style={{ background:'none', border:'none', cursor:'pointer', color:'#A8A1B0',
                      fontSize:13, fontFamily:"'DM Sans',sans-serif", padding:0, marginBottom:14,
                      display:'flex', alignItems:'center', gap:6 }}>
                    ← Voltar
                  </button>
                  {!isMobile && (
                    <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:24,
                      color:'#3D3447', marginBottom:14 }}>Cadastro de Cliente</div>
                  )}

                  <button type="button" onClick={handleGoogle} disabled={googleLoading}
                    style={{ width:'100%', padding:'13px 16px', background:'#fff',
                      border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, fontWeight:500,
                      cursor: googleLoading ? 'not-allowed' : 'pointer',
                      display:'flex', alignItems:'center', justifyContent:'center', gap:10,
                      color:'#3D3447', marginBottom:14, fontFamily:"'DM Sans',sans-serif",
                      opacity: googleLoading ? 0.7 : 1 }}>
                    <GoogleIcon />
                    {googleLoading ? 'Conectando...' : 'Cadastrar com Google'}
                  </button>

                  <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14 }}>
                    <div style={{ flex:1, height:1, background:'#E8E5EC' }} />
                    <span style={{ fontSize:12, color:'#C0BAC8' }}>ou preencha os dados</span>
                    <div style={{ flex:1, height:1, background:'#E8E5EC' }} />
                  </div>

                  <form onSubmit={handleRegister}>
                    <div style={{ marginBottom:11 }}>
                      <label style={{ display:'block', fontSize:13, fontWeight:500,
                        color:'#6B6070', marginBottom:5 }}>Nome completo *</label>
                      <input type="text" value={regName}
                        onChange={e => setRegName(e.target.value)} required
                        placeholder="Seu nome completo"
                        autoComplete="name" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom:11 }}>
                      <label style={{ display:'block', fontSize:13, fontWeight:500,
                        color:'#6B6070', marginBottom:5 }}>E-mail *</label>
                      <input type="email" value={regEmail}
                        onChange={e => setRegEmail(e.target.value)} required
                        placeholder="seu@email.com"
                        autoComplete="email" style={inputStyle} />
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:11 }}>
                      <div>
                        <label style={{ display:'block', fontSize:13, fontWeight:500,
                          color:'#6B6070', marginBottom:5 }}>Telefone</label>
                        <input type="tel" value={regPhone}
                          onChange={e => setRegPhone(e.target.value)}
                          placeholder="(11) 99999-9999"
                          autoComplete="tel" style={inputStyle} />
                      </div>
                      <div>
                        <label style={{ display:'block', fontSize:13, fontWeight:500,
                          color:'#6B6070', marginBottom:5 }}>
                          CPF <span style={{ fontSize:11, color:'#A8A1B0', fontWeight:400 }}>(opcional)</span>
                        </label>
                        <input type="text" value={regCPF}
                          onChange={e => setRegCPF(formatCPF(e.target.value))}
                          placeholder="000.000.000-00"
                          maxLength={14}
                          style={{ ...inputStyle,
                            borderColor: regCPF && regCPF.replace(/\D/g,'').length === 11
                              && !isValidCPF(regCPF) ? '#C9929A' : '#E8E5EC' }} />
                        {regCPF && regCPF.replace(/\D/g,'').length === 11 && !isValidCPF(regCPF) && (
                          <span style={{ fontSize:11, color:'#C9929A', marginTop:3, display:'block' }}>
                            CPF inválido
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ marginBottom:11 }}>
                      <label style={{ display:'block', fontSize:13, fontWeight:500,
                        color:'#6B6070', marginBottom:5 }}>
                        Senha * <span style={{ fontSize:11, color:'#A8A1B0', fontWeight:400 }}>(mín. 6 caracteres)</span>
                      </label>
                      <input type="password" value={regPass}
                        onChange={e => setRegPass(e.target.value)} required
                        placeholder="••••••••"
                        autoComplete="new-password" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom:18 }}>
                      <label style={{ display:'block', fontSize:13, fontWeight:500,
                        color:'#6B6070', marginBottom:5 }}>Confirmar senha *</label>
                      <input type="password" value={regPass2}
                        onChange={e => setRegPass2(e.target.value)} required
                        placeholder="••••••••"
                        autoComplete="new-password"
                        style={{ ...inputStyle,
                          borderColor: regPass2 && regPass !== regPass2 ? '#C9929A' : '#E8E5EC' }} />
                      {regPass2 && regPass !== regPass2 && (
                        <span style={{ fontSize:11, color:'#C9929A', marginTop:3, display:'block' }}>
                          As senhas não coincidem
                        </span>
                      )}
                    </div>
                    <button type="submit" disabled={regLoading} style={btnPrimary(regLoading)}>
                      {regLoading ? 'Criando conta...' : 'Criar minha conta →'}
                    </button>
                  </form>

                  <p style={{ textAlign:'center', fontSize:13, color:'#A8A1B0', marginTop:14 }}>
                    Já tem conta?{' '}
                    <button onClick={() => { setTab('login'); resetRegForm(); }}
                      style={{ background:'none', border:'none', color:'#6B5876', fontWeight:600,
                        cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontSize:13 }}>
                      Entrar
                    </button>
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
